import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../services/api.service';
import { environment } from '../../../environments/environment';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { init } from '../editorconfig';
import { Subscription } from 'rxjs';
import { FileUpload } from 'primeng/fileupload';
import { getFeasibilityStudyTemplate } from '../../pre-ready-tepmlate';

@Component({
  selector: 'app-feasibilty-study',
  standalone: false,
  templateUrl: './feasibilty-study.component.html',
  styleUrl: './feasibilty-study.component.scss',
})
export class FeasibiltyStudyComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  // ========================
  // Inputs & Outputs
  // ========================
  @Input() step: any;
  @Input() id: any;
  @Input() getUrl1: any;
  @Input() postUrl1: any;
  @Input() isEditable: boolean = false;
  @Input() stepDetails: any;
  @Input() schemeType: any;

  @Output() IRA: EventEmitter<any> = new EventEmitter<any>();
  @Output() generatePDFEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() routeConfig: EventEmitter<any> = new EventEmitter<any>();
  @Output() submitProposal: EventEmitter<any> = new EventEmitter<any>();
  @Output() stepCompleted: EventEmitter<void> = new EventEmitter<void>();

  // ========================
  // ViewChild
  // ========================
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  // ========================
  // Properties
  // ========================
  baseUrl: string = environment.APIUrl;
  isEditorDisabled: boolean = false;
  init: EditorComponent['init'] = init;
  createMode: boolean = true;
  uploadedFileName: string = '';
  uploadedUrl: any;

  proposalForm!: FormGroup;
  challengeForm!: FormGroup;
  commentForm!: FormGroup;

  file!: File;
  eoiData: any;
  needGenData: any;
  apiUrl: string = environment.APIUrl;
  userID: any;

  payload!: {
    proposal_id: any;
    proposal_name: any;
  };

  private subscriptions: Subscription = new Subscription();

  editorReady: boolean = false;

  // ========================
  // Constructor
  // ========================
  constructor(
    private messageService: MessageService,
    private fb: FormBuilder,
    private api: ApiService,
    private route: ActivatedRoute
  ) {
    const role = sessionStorage.getItem('user_role_id');
    this.userID = role;
  }

  // ========================
  // Lifecycle Hooks
  // ========================
  ngOnInit(): void {
    this.route.queryParams.subscribe((params: any) => {
      const proposalId = params['id'];
      const proposalName = params['name'];
      this.payload = {
        proposal_id: proposalId,
        proposal_name: proposalName,
      };
      this.commentForm = this.fb.group({
        comment: [''],
      });
      this.commentForm.disable();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['id'] || changes['step']) && this.id && this.payload?.proposal_id) {
      if (!changes['id']?.firstChange && !changes['step']?.firstChange) {
        if (changes['id']?.currentValue !== changes['id']?.previousValue) {
          this.getProposalData();
          this.getStep1();
        }
      }
      // Initial load handled in ngAfterViewInit
    }
  }

  ngAfterViewInit(): void {
    if (this.id && this.payload?.proposal_id && !this.eoiData) {
      this.getProposalData();
      this.getStep1();
    }
    setTimeout(() => {
      if (this.stepDetails?.can_edit) {
        this.commentForm.enable();
      } 
    }, 3000);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ========================
  // Editor Methods
  // ========================
  rebuildEditor(): void {
    this.editorReady = false; // destroy
    setTimeout(() => {
      this.init = this.getTinyInit(); // refresh config
      this.editorReady = true; // recreate editor
    }, 0);
  }

  getTinyInit(): EditorComponent['init'] {
    return {
      readonly: this.isEditorDisabled,
    };
  }

  // ========================
  // Data Fetch/Manipulation
  // ========================
  getProposalData(): void {
    if (!this.payload?.proposal_id) {
      return;
    }
    const stepID = sessionStorage.getItem('needStepID');
    let url: string;
    if (this.schemeType === 'rev') {
      url = `/revenue/need-generation/?proposal_step=${stepID}&proposal_step__proposal=${this.payload.proposal_id}`;
    } else if (this.schemeType === 'tdf') {
      url = `/tdf/need-generation/?proposal_step__proposal=${this.payload.proposal_id}`;
    } else {
      url = `/make1/need-generation/?proposal_step__proposal=${this.payload.proposal_id}`;
    }
    const sub = this.api.getAPI(url).subscribe((res: any) => {
      this.needGenData = res[0];
    });
    this.subscriptions.add(sub);
  }

  getStep1(): void {
    if (!this.id || !this.payload?.proposal_id) {
      return;
    }
    const sub = this.api.getAPI( `/make1/feasibility/study/?proposal_step__proposal=${this.payload.proposal_id}&proposal_step=${this.id}`)
      .subscribe((res: any) => {
        this.eoiData = res;
        console.log('this is eoi data in feasibility study', this.eoiData.comment);
        this.commentForm.patchValue({
          comment: this.eoiData?.comment ? this.eoiData.comment : getFeasibilityStudyTemplate(this.payload?.proposal_name, this.stepDetails),
        });
      });
    this.subscriptions.add(sub);
  }



  // ========================
  // Utilities
  // ========================
  getFileName(url: string): string {
    const fileName = url.split('/').pop() || 'Unknown File';
    return fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName;
  }

  toggleCreateMode(): void {
    this.createMode = !this.createMode;
  }

  clearUpload(fileUpload: FileUpload): void {
    this.uploadedFileName = '';
    this.uploadedUrl = null;
    fileUpload.clear();
  }

  // ========================
  // File Upload
  // ========================
  onUpload(event: any): void {
    const file = event.files?.[0];
    this.uploadedFileName = file.name;
    this.file = file;
  }

  // ========================
  // Submission
  // ========================
  onSubmit(): void {
    const formData = new FormData();
    formData.append('proposal_step', this.step.id);
    formData.append('comment', this.commentForm.get('comment')?.value);
    // formData.append('file', this.file);
    if (this.eoiData) {
      const sub = this.api
        .putAPI(`/make1/feasibility/study/${this.eoiData.id}/`, formData)
        .subscribe((res: any) => {
          this.fileUpload.clear();
          this.uploadedFileName = '';
          this.getStep1();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message || 'Report updated successfully',
          });
        });
      this.subscriptions.add(sub);
    } else {
      const sub = this.api
        .postAPI(`/make1/feasibility/study/`, formData)
        .subscribe((res: any) => {
          this.fileUpload.clear();
          this.uploadedFileName = '';
          this.getStep1();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message || 'Report updated successfully',
          });
        });
      this.subscriptions.add(sub);
    }
  }

  // ========================
  // PDF Generation & Step Completion
  // ========================
  generatePDF(): void {
    const payload = {
      url: '/revenue/need/version-control/',
      data: [
        { key: 'Proposal Name', value: this.payload.proposal_name },
        { key: 'Comment', value: this.commentForm.get('comment')?.value },
      ],
    };
    this.generatePDFEmitter.emit(payload);
  }

  goToNextStep(): void {
    this.stepCompleted.emit();
  }


}
